package dev.lingvo.site.pages

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.maxWidth
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import dev.lingvo.site.components.marketing.DocEntry
import dev.lingvo.site.components.marketing.DocsList
import dev.lingvo.site.components.marketing.DocsProse
import dev.lingvo.site.components.marketing.DocsSubheading
import dev.lingvo.site.components.marketing.MarketingDocPage
import dev.lingvo.site.ds.LAlert
import dev.lingvo.site.ds.LAlertVariant
import dev.lingvo.site.ds.LTable
import dev.lingvo.site.ds.LTableBody
import dev.lingvo.site.ds.LTableCell
import dev.lingvo.site.ds.LTableHead
import dev.lingvo.site.ds.LTableHeaderCell
import dev.lingvo.site.ds.LTableRow
import dev.lingvo.site.ds.toSitePalette
import kotlinx.browser.document
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.dom.Text
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.tr

/**
 * The privacy policy.
 *
 * Written to describe what the system actually does rather than to reserve every right a lawyer
 * could think of: the sub-processor list is the real deployment, and the "what we do not collect"
 * section is checkable against the shipped bundle, which loads no third-party script.
 */
@Page("/privacy")
@Composable
fun PrivacyPage() {
    LaunchedEffect(Unit) {
        document.title = tr("privacy.privacy_lingvo", "Privacy — Lingvo")
    }
    MarketingDocPage(
        eyebrow = "Privacy",
        title = t("privacy.what_we_hold_where_it_lives", "What we hold, where it lives, and who else can see it."),
        lede = "Lingvo stores the strings you send it and the account that sent them. There is no " +
            "advertising business here and no analytics vendor — the only reason we hold anything " +
            "is to run the product you are paying for.",
        entries = privacyEntries(),
        footnote = "Last updated 10 August 2026. Material changes are announced by email to " +
            "workspace owners before they take effect.",
    )
}

internal fun privacyEntries(): List<DocEntry> = listOf(
    DocEntry("collect", tr("privacy.what_we_collect", "What we collect")) {
        DocsSubheading(tr("privacy.your_account", "Your account"))
        DocsProse(
            tr("privacy.your_name_email_address_and_a", "Your name, email address and a password hash. We never store the password itself. If " +
                "you joined by invitation we also keep who invited you, so a workspace owner can " +
                "see how someone got access."),
        )
        DocsSubheading(tr("privacy.your_workspace_content", "Your workspace content"))
        DocsProse(
            tr("privacy.the_keys_source_strings_translations_comments", "The keys, source strings, translations, comments, glossary terms and file paths you " +
                "push or type. This is the product — it is stored so it can be edited, exported " +
                "and pulled back into your repository."),
        )
        DocsSubheading(tr("privacy.operational_records", "Operational records"))
        DocsProse(
            tr("privacy.setup_failure_counts", "We also keep operational log summaries of failed signup, setup and import requests. " +
                "These summaries contain only the type of operation, a broad failure category and a count. " +
                "They do not contain account identifiers, request bodies or customer strings."),
        )
        DocsProse(
            tr("privacy.signup_source_labels", "If you use a tagged signup link, we keep its broad source label, such as GitHub, " +
                "alongside the signup method and the link between your account and its original workspace. " +
                "Attribution records do not contain referring URLs or free-form tags. The records stay " +
                "with the workspace; deleting your account removes the link to you."),
        )
        DocsProse(
            tr("privacy.first_party_milestones", "To understand whether setup works, we keep the first time your workspace prepares an " +
                "export or completes a QA check. These records contain only a workspace identifier, " +
                "the type of action and its time. They contain no strings, file paths or individual " +
                "user identifiers, and are removed when the workspace is deleted."),
        )
        DocsProse(
            tr("privacy.request_logs_error_traces_and_an", "Request logs, error traces and an activity trail of who changed what and when — " +
                "including the times we changed something ourselves, which appear on your audit " +
                "page under a support badge. Logs include IP addresses, which is unavoidable when " +
                "running a network service and is what makes abuse and account compromise investigable."),
        )
    },

    DocEntry("not-collected", tr("privacy.what_we_do_not_collect", "What we do not collect")) {
        DocsList(
            listOf(
                "No third-party analytics, no advertising network, and no script from any third party.",
                // The fonts are the one third-party request there is, and a privacy policy is the
                // wrong place to be quietly technically-correct about it: a stylesheet is not a
                // script, but it still tells Google the IP address of everyone who visits.
                "Two third-party requests, both named. The typefaces come from Google Fonts, so " +
                    "Google sees the IP address of anyone loading a page — we intend to self-host " +
                    "them and remove this. And the checkout page loads Paddle's payment form from " +
                    "Paddle, which is how your card details reach them and never reach us. Nothing " +
                    "else on any page is fetched from anyone but us.",
                "No advertising or marketing cookies, and no cross-site tracking.",
                "No tracking pixels, web beacons, read receipts or click tracking in email. Our " +
                    "messages are HTML with a plain-text twin, and they load nothing remote — so " +
                    "opening one tells us nothing at all.",
                "No payment card numbers — if and when card payments are added, the processor " +
                    "holds those details and we never see them.",
            ),
        )
        // **"Three things" was wrong, and the one it left out is workspace content.**
        //
        // The app writes eight keys — `lingvo:token`, `lingvo:colorMode` and
        // `lingvo:colorModeExplicit`, `lingvo:uiLocale`, `lingvo:workspace`,
        // `lingvo:editor:mt-provider:{slug}`, `lingvo:translation-draft:v1:{…}` and
        // `lingvo:pendingInvite`. The draft key holds the translation itself, `value` and every
        // plural form, so the page whose whole job is enumerating what is stored and where was the
        // one page not saying that some of it is the customer's own strings.
        //
        // "Briefly" was wrong too: `lingvo:pendingInvite` is removed only on a *successful*
        // accept, so somebody who opens an invitation and walks away keeps it indefinitely.
        //
        // `lingvo:apiUrl` is deliberately absent: nothing in the app writes it. It is a
        // development override a person sets by hand, and listing it here would describe the
        // product to a reader as doing something it does not do.
        DocsProse(
            tr("privacy.the_app_keeps_seven_things_in", "The app keeps seven things in your browser's local storage. None of them is a cookie " +
                "and none is sent anywhere but to Lingvo: your session token; your light or dark " +
                "theme choice; your interface language; which workspace you last opened; which " +
                "machine-translation engine you last used in a project; an invitation token, from " +
                "the moment you open an invitation link until you accept it; and — the one worth " +
                "naming on its own — translation drafts."),
        )
        DocsProse(
            tr("privacy.that_last_one_is_your_own", "That last one is your own content. While you are editing a translation, the text you " +
                "have typed but not yet saved is kept in local storage against the project, " +
                "locale and key, so closing the tab or losing your connection does not lose the " +
                "work. It stays on your machine, it reaches us only when the edit is saved like " +
                "any other edit, and it is removed as soon as that save lands. Clearing site data " +
                "for Lingvo in your browser removes all of this."),
        )
    },

    DocEntry("where", tr("privacy.where_it_lives", "Where it lives")) {
        DocsProse(
            tr("privacy.the_database_and_api_both_run", "The database and API both run in Sydney, Australia. Your strings are stored there " +
                "and nowhere else. They leave that region in exactly two circumstances, both listed " +
                "below: a sub-processor that states otherwise in the table, and a machine " +
                "translation you asked for."),
        )
        LTable(caption = t("privacy.sub_processors", "Sub-processors")) {
            LTableHead {
                LTableRow {
                    LTableHeaderCell { Text(t("privacy.provider", "Provider")) }
                    LTableHeaderCell { Text(t("privacy.what_it_handles", "What it handles")) }
                    LTableHeaderCell { Text(t("privacy.where", "Where")) }
                }
            }
            LTableBody {
                SubProcessor("Neon", tr("privacy.the_postgres_database_every_string_and", "The Postgres database — every string and account record."), "AWS ap-southeast-2, Sydney")
                SubProcessor("Fly.io", tr("privacy.runs_the_api_holds_no_data", "Runs the API. Holds no data at rest beyond logs."), "syd, Sydney")
                SubProcessor("Cloudflare", tr("privacy.serves_the_website_and_terminates_tls", "Serves the website and terminates TLS."), "Global edge")
                SubProcessor("Brevo", tr("privacy.sends_transactional_email_resets_invitations_ver", "Sends transactional email: resets, invitations, verification."), "EU")
                // Paddle is the merchant of record, not a payment gateway we pass cards through:
                // the contract for a paid plan is between the customer and Paddle. That is a
                // stronger statement than "we use a processor" and it belongs on this page, because
                // it is also why no card number ever touches Lingvo.
                // Ours, not the customer's, and therefore genuinely a sub-processor rather than a
                // provider they chose. The other five engines are not listed: a workspace only
                // reaches those with a key of its own, which makes the relationship theirs.
                SubProcessor(
                    "OpenAI",
                    "Machine translation for projects with no provider key of their own. Sees only " +
                        "the source strings of a translation you asked for.",
                    "United States",
                )
                SubProcessor(
                    "Paddle",
                    "Merchant of record for paid plans. Takes payment, issues invoices and handles " +
                        "tax. Sees your billing details; we never see a card number.",
                    "Global",
                )
            }
        }
    },

    DocEntry("machine-translation", tr("privacy.machine_translation_and_your_strings", "Machine translation and your strings")) {
        DocsProse(
            tr("privacy.nothing_leaves_lingvo_for_a_machine", "Nothing leaves Lingvo for a machine-translation provider until you ask for it. When " +
                "you run a pre-translate or lingvo translate, the source strings for the keys you " +
                "selected are sent to the provider you chose, and its reply comes back as a draft."),
        )
        DocsProse(
            tr("privacy.which_provider_that_is_is_your", "Which provider that is, is your choice: Claude, OpenAI, DeepL, Google, LibreTranslate " +
                "or MyMemory. Each has its own terms about retention and training, and those terms " +
                "— not ours — govern what it does with the text once it arrives. If a provider's " +
                "policy matters to you, choose accordingly, or do not use machine translation at " +
                "all. Everything else in Lingvo works without it."),
        )
        DocsProse(
            tr("privacy.with_one_exception_and_it_is", "With one exception, and it is the default on the Free plan. A project that has not " +
                "been given a provider key of its own runs on Lingvo's own OpenAI account, so that " +
                "the credits included with your plan are usable without you signing up to anything. " +
                "In that case the provider is OpenAI and the choice is ours, not yours — which is " +
                "why OpenAI is listed as a sub-processor above and the others are not. Add your own " +
                "key for any provider and the request goes to your account instead, under your " +
                "terms."),
        )
        DocsProse(
            tr("privacy.a_workspace_that_uses_only_its", "A workspace that uses only its own keys sends nothing to an account of ours. A " +
                "workspace that never runs a machine translation sends nothing to any of them."),
        )
        LAlert(
            title = t("privacy.your_provider_keys_are_encrypted_and", "Your provider keys are encrypted and never readable back"),
            description = tr("privacy.a_key_you_save_is_encrypted_2", "A key you save is encrypted with AES-GCM before it is written to the " +
                "database and is never returned by the API — not to you, not to an admin, not to " +
                "the CLI. The interface shows only that a key is present."),
            modifier = Modifier.fillMaxWidth().maxWidth(44.cssRem),
            variant = LAlertVariant.INFO,
        )
    },

    DocEntry("access", tr("privacy.who_can_see_your_content", "Who can see your content")) {
        DocsProse(
            tr("privacy.members_of_your_workspace_according_to", "Members of your workspace, according to the role you gave them. Owners and admins can " +
                "change structure; translators can write translations; viewers can only read."),
        )
        DocsProse(
            tr("privacy.on_our_side_access_to_the", "On our side, access to the production database is limited to the people operating the " +
                "service, is used for support and incident response, and is not used to read " +
                "workspace content otherwise. We do not sell data, and we do not train models on " +
                "your strings."),
        )
    },

    DocEntry("changes-we-make", tr("privacy.what_we_can_change_and_how", "What we can change, and how you see it")) {
        DocsProse(
            tr("privacy.a_short_fixed_list_of_things", "A short, fixed list of things a person at Lingvo can do inside your workspace, and " +
                "only after you have asked us about something. We can add machine-translation " +
                "credits — for example to put back credits a failed run consumed, or after a " +
                "billing mistake. We can end a run that is stuck, which returns the credits it " +
                "reserved. We can end every sign-in, API token and agent connection belonging to " +
                "one person, which is what we do when an account is compromised — and we email " +
                "that person when we do. And we can re-run one of our own scheduled reconciliation " +
                "jobs early."),
        )
        DocsProse(
            tr("privacy.adding_credits_appears_on_your_workspaces", "Adding credits appears on your workspace's audit page, badged support, with the " +
                "reason we were given. Ending somebody's sign-ins deliberately does not: it " +
                "concerns one person, and putting it on a page every admin reads would tell their " +
                "colleagues something about them that has nothing to do with the work. That person " +
                "is emailed instead."),
        )
        LAlert(
            title = t("privacy.there_is_no_way_for_us", "There is no way for us to sign in as you"),
            description = tr("privacy.no_route_no_button_and_no_2", "No route, no button and no column that would allow it. We also cannot " +
                "read a provider key, an API token or a password — the first is encrypted and " +
                "returned by nothing, and the other two are stored only as hashes."),
            modifier = Modifier.fillMaxWidth().maxWidth(44.cssRem),
            variant = LAlertVariant.INFO,
        )
        DocsProse(
            tr("privacy.we_keep_our_own_record_of", "We keep our own record of every one of these actions, separately from yours, including " +
                "an internal note saying why. That record is described under Keeping and deleting below."),
        )
    },

    DocEntry("retention", tr("privacy.keeping_and_deleting", "Keeping and deleting")) {
        DocsList(
            listOf(
                "Workspace content is kept until you delete it or close the account.",
                "Deleting a project removes its keys and translations; the deletion is not " +
                    "reversible from the interface.",
                "Operational logs are kept for a limited period and then discarded.",
                "Our own record of anything a person at Lingvo did inside your workspace is kept " +
                    "separately from your audit log and for longer than it. It has to outlive your " +
                    "copy, because the question it answers is what we did to you.",
                "That record carries an internal note about why we acted, which is not shown to " +
                    "you. It routinely refers to other customers — an outage, a shared root cause " +
                    "— which is exactly why it is a separate field from the reason you are shown " +
                    "on your audit page.",
                "Password-reset, invitation and verification tokens are stored hashed and expire " +
                    "on a fixed schedule.",
                "API tokens are stored hashed. A token shown once at creation cannot be recovered, " +
                    "only revoked and replaced.",
            ),
        )
        DocsProse(
            tr("privacy.our_database_host_keeps_point_in", "Our database host keeps point-in-time recovery snapshots, and a deleted record can " +
                "briefly survive in those. They roll over on the host's own schedule rather than " +
                "being edited to remove single rows. We do not run a separate backup of our own."),
        )
    },

    DocEntry("rights", tr("privacy.your_rights", "Your rights")) {
        DocsProse(
            tr("privacy.you_can_export_everything_lingvo_export", "You can export everything: lingvo export --bundle writes every locale in the format " +
                "you configured, and needs no plan, no credits and no permission from us. That is " +
                "deliberate — a localization tool you cannot leave is a hostage situation, not a " +
                "product."),
        )
        DocsProse(
            tr("privacy.you_can_also_ask_us_to", "You can also ask us to correct or delete your personal data, or to tell you what we " +
                "hold. Depending on where you live you may have further rights under the GDPR, the " +
                "Australian Privacy Act or comparable law; we apply the same process to everyone " +
                "regardless of which one applies."),
        )
    },

    DocEntry("contact", tr("privacy.contact", "Contact")) {
        DocsProse(
            tr("privacy.write_to_privacy_lingvo_dev_for", "Write to privacy@lingvo.dev for anything on this page, including access, correction " +
                "and deletion requests. We answer from a person, not a ticket queue."),
        )
    },
)

@Composable
private fun SubProcessor(name: String, purpose: String, where: String) {
    LTableRow {
        LTableCell(Modifier.styleModifier { property("white-space", "nowrap") }) { Text(name) }
        LTableCell(Modifier.color(ColorMode.current.toSitePalette().textSecondary)) { Text(purpose) }
        LTableCell(Modifier.color(ColorMode.current.toSitePalette().textSecondary)) { Text(where) }
    }
}
